import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Theme } from '@material-ui/core/styles/createTheme';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import List from '@material-ui/core/List';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';

import Info from 'components/Tooltip/Info';

import useStyles from './filterMenuStyles';

interface Styling {
  root: string;
  item: string;
  selected: string;
  icon: string;
  drop: string;
  primary: string;
}

interface StyleProps {
  fontSize?: string;
}

interface Props {
  items: { value: string | number; label: string; info?: string; color?: string }[];
  onClick: Function;
  currentValue: string | number;
  mobile?: string;
  styling?: Styling;
  filterStyles?: Partial<StyleProps>;
  autoSize?: boolean;
  selectText?: string;
  disabled?: boolean;
}

const FilterMenu: React.FC<Props> = ({
  items,
  currentValue,
  onClick,
  mobile,
  styling,
  filterStyles,
  selectText,
  disabled,
  autoSize = true,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { t } = useTranslation();

  const defaultStyling = useStyles(filterStyles || {});
  const classes = styling || defaultStyling;

  const width = items.length ? 100 / items.length : 0;
  const mobileVersion = useMediaQuery(
    (theme: Theme) => (mobile ? `@media (max-width: ${mobile}px)` : theme.breakpoints.down('sm')),
    { noSsr: true },
  );
  const selectedText = items.filter((i) => currentValue === i.value)[0]?.label || selectText || '';

  return items.length ? (
    <>
      <List className={classes.root}>
        {!mobileVersion &&
          items.map(({ label, value, info }) => {
            const selected = currentValue === value;
            return (
              <Fragment key={label}>
                <MenuItem
                  onClick={() => onClick(value)}
                  className={`${classes.item} ${selected ? classes.selected + ' selected' : ''} ${value}`}
                  style={autoSize ? { width: `${width}%` } : undefined}
                  disabled={disabled}
                >
                  <ListItemText
                    classes={{
                      primary: classes.primary,
                    }}
                  >
                    {t(label)}
                    {info && <Info content={t(info)} button />}
                  </ListItemText>
                </MenuItem>
              </Fragment>
            );
          })}
        {mobileVersion && (
          <MenuItem
            onClick={(event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget)}
            className={`${classes.item} ${classes.drop} ${currentValue} selected`}
            style={{ width: `100%` }}
            disabled={disabled}
          >
            <ListItemIcon className={classes.icon}>
              <ArrowDropDownIcon htmlColor="#ccc" />
            </ListItemIcon>
            <ListItemText primary={t(selectedText)} />
          </MenuItem>
        )}
      </List>

      {mobileVersion && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
          getContentAnchorEl={null}
          transitionDuration={0}
        >
          {items.map(({ label, value, info }) => {
            const selected = currentValue === value;
            return (
              <MenuItem
                key={label}
                onClick={() => {
                  onClick(value);
                  setAnchorEl(null);
                }}
                className={`${selected ? classes.selected : ''}`}
              >
                <ListItemText>
                  {t(label)}
                  {info && <Info content={t(info)} button />}
                </ListItemText>
              </MenuItem>
            );
          })}
        </Menu>
      )}
    </>
  ) : null;
};

export default FilterMenu;
